// Entry: one module graph, one rAF loop. Every init is a no-op on pages without its DOM.
import { counters, Giant, lazyArt, magnetic, reveals, root, start } from "./core.ts"
import { initEstimator } from "./estimator.ts"
import { initGlyphs, initHero, initMarquee, initPeek, initPin } from "./home.ts"
import { initListing } from "./listing.ts"
import { initMapping } from "./mapping.ts"
import { initNav } from "./nav.ts"
import { initBars, initSecurity } from "./security.ts"
import { initWaitlist } from "./waitlist.ts"
import { initYardPath } from "./yard-path.ts"

/** Hand the main thread back to the browser between setup steps, so input and paint aren't held up. */
const yieldToBrowser = (): Promise<void> => {
  const scheduler = (globalThis as { scheduler?: { yield?: () => Promise<void> } }).scheduler
  return scheduler?.yield ? scheduler.yield() : new Promise((resolve) => setTimeout(resolve, 0))
}

const boot = async () => {
  // What's on screen first: the loop, the nav and the hero.
  start()
  initNav()
  initHero()
  requestAnimationFrame(() => root.classList.add("ready"))
  // The footer wordmark measures Funnel Display's ink, so it starts as soon as that face is loaded,
  // alongside the steps below rather than after them.
  const giant = document.getElementById("giant")
  if (giant instanceof HTMLCanvasElement) {
    void document.fonts.load('700 100px "Funnel Display"').then(() => new Giant(giant))
  }
  // Then everything else, one step at a time. On a phone CPU running these back to back was one long
  // task of several hundred milliseconds.
  const rest = [
    initMarquee,
    initPin,
    initGlyphs,
    initPeek,
    initYardPath,
    initListing,
    initWaitlist,
    initMapping,
    initEstimator,
    initSecurity,
    initBars,
    () => lazyArt(),
    () => reveals(),
    () => magnetic(),
    () => counters(),
  ]
  for (const init of rest) {
    await yieldToBrowser()
    init()
  }
}

void ("fonts" in document ? document.fonts.ready : Promise.resolve()).then(boot)
